/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable max-len */
/* eslint-disable no-underscore-dangle */

'use-strict';

import { IScope } from 'angular';
import { SalesAPIService } from '../../../shared/api/sales-api.service';
import { NumberConversionService } from '../../../shared/converter/number-converter.service';
import { UsersService } from '../../../shared/users/users.service';
import { ConfigService, ConstantTypes } from '../../../shared/config/config.service';
import { EAvailabilityCode } from '../../../src/app/@shared-types/vehicle/enums';
import { VehicleReservationRequest, VehicleSellRequest } from '../../../src/app/@shared-types/vehicle/availability';
import { AutoboardVehicle } from '../../../src/app/@shared-types/vehicle';
import LogService from '../../../src/app/shared/services/log';
import { TradeIn } from '../../../src/app/@shared-types/vehicle/tradein';
import { VehicleFormComponentExitFunction } from '../../../src/app/@types/vehicle';
import { EConstantType } from '../../../src/app/@shared-types/config/enums';
import { FileInput } from '../../../src/app/@types';
import { FileService, FileServiceUploadRequest } from '../../../shared/storage/file.service';
import { EFileSection } from '../../../src/app/@shared-types/misc/file';

interface AvailabilityScope extends IScope {
  vehicle: AutoboardVehicle,
  customerId?: string,
  exit: (data: VehicleFormComponentExitFunction) => void,
}

/* @ngInject */
export default class AvailabilityFormController {
  private sending = false;

  private maxReservationDuration: number;

  private exit: (data: VehicleFormComponentExitFunction) => void;

  private vehicle!: AutoboardVehicle;

  private $scope: AvailabilityScope;

  private usersService: UsersService;

  private salesApiService: SalesAPIService;

  private numberConversionService: NumberConversionService;

  private fileService: FileService;

  private logService: LogService;

  private availabilityData: {
    /** Shared data */
    customerId?: string,
    seller: number,
    code: EAvailabilityCode,
    /** Reservation | Event */
    endDate: Date | null,
    /** Sell */
    contractDate: Date | null,
    hasTradeIn: boolean,
    plannedHandover: Date | null,
    tradeIn?: Pick<TradeIn, 'make' | 'model' | 'vin' | 'plannedUsage'>,
    tradeInVisible: boolean,
    userId: string | null,
    vehicleContract?: FileInput,
  };

  /** Selectable list of users for admins only. */
  private userFilter: { [key: string]: string } | null = null;

  constructor(
    $scope: AvailabilityScope,
    numberConversionService: NumberConversionService,
    salesApiService: SalesAPIService,
    usersService: UsersService,
    configService: ConfigService,
    logService: LogService,
    fileService: FileService,
  ) {
    this.usersService = usersService;
    this.salesApiService = salesApiService;
    this.numberConversionService = numberConversionService;
    this.logService = logService;
    this.$scope = $scope;
    this.fileService = fileService;

    this.maxReservationDuration = configService.get(EConstantType.MAXIMUM_RESERVATION_DAYS) || 10;
    this.vehicle = this.$scope.vehicle;
    this.exit = this.$scope.exit;

    this.availabilityData = {
      code: EAvailabilityCode.Available,
      contractDate: null,
      customerId: this.$scope.customerId,
      endDate: null,
      hasTradeIn: false,
      plannedHandover: null,
      seller: -1,
      tradeInVisible: false,
      userId: null,
    };

    /** Show seller select options only if current user is admin
     * or it is enabled globally in Tenant's config. */
    if (
      this.usersService.currentUserAdmin()
      || configService.get(EConstantType.EDIT_SELLER)
    ) {
      this.availabilityData.userId = String(this.usersService.currentUserId());
      this.userFilter = {};
      usersService.getUsers()
        .forEach((u) => {
          (this.userFilter as any)[u.id] = `${u.first_name} ${u.last_name}`;
        });
    }
  }

  public exitForm = (): void => {
    this.exit({ removeLock: true });
  };

  private reserveVehicle = async (): Promise<void> => {
    const { availabilityData } = this;
    if (!availabilityData.customerId) return;

    const reservationRequest: VehicleReservationRequest = {
      customerId: availabilityData.customerId,
      endDate: this.numberConversionService.frontendStringToDate(String(availabilityData.endDate)),
      type: EAvailabilityCode.Reserved,
      userId: this.availabilityData.userId ? parseInt(this.availabilityData.userId, 10) : this.usersService.currentUserId(),
      vehicleId: String(this.vehicle._id),
    };

    await this.salesApiService.postReservation(reservationRequest);
    this.exit({ removeLock: false });
  };

  private sellVehicle = async () => {
    const { availabilityData } = this;
    if (!availabilityData.customerId) return;

    const sellRequest: VehicleSellRequest = {
      contractDate: this.numberConversionService.frontendStringToDate(String(availabilityData.contractDate)),
      customerId: availabilityData.customerId,
      hasTradeIn: availabilityData.hasTradeIn || false,
      plannedHandover: this.numberConversionService.frontendStringToDate(String(availabilityData.plannedHandover)),
      userId: this.availabilityData.userId ? parseInt(this.availabilityData.userId, 10) : this.usersService.currentUserId(),
      vehicleId: String(this.vehicle._id),
    };

    if (this.availabilityData.vehicleContract?.file) {
      const fileRequest: FileServiceUploadRequest = {
        identifier: String(this.vehicle._id),
        section: EFileSection.VEHICLE,
        type: this.availabilityData.vehicleContract.fileType,
      };

      await this.fileService
        .uploadContextualFile(this.availabilityData.vehicleContract.file, this.availabilityData.vehicleContract.progressBar, fileRequest);
    }

    await this.salesApiService.postSale(sellRequest)
      .then(() => {
        this.sending = false;
        if (!this.availabilityData.tradeIn) {
          return Promise.resolve(0);
        }
        const tradeIn: Partial<TradeIn> = {
          customerId: sellRequest.customerId,
          make: availabilityData.tradeIn!.make,
          model: availabilityData.tradeIn!.model,
          vin: availabilityData.tradeIn!.vin,
          vehicleId: String(this.vehicle._id),
          plannedUsage: availabilityData.tradeIn!.plannedUsage,
        };

        return this.salesApiService.postTradeIn(tradeIn);
      });

    this.exit({ removeLock: false });
  };

  public formTransmission = (): Promise<void> => {
    this.sending = true;

    if (this.availabilityData.code === EAvailabilityCode.Sold) return this.sellVehicle();
    return this.reserveVehicle();
  };

  /**
   * User selects or deselects that the car has a trade in.
   * Add or remove attribute of changed values.
   */
  public toggleTradeIn = (): void => {
    if (this.availabilityData.tradeInVisible) {
      this.availabilityData.tradeIn = {
        make: '',
        model: '',
        vin: '',
        plannedUsage: '',
      };
      return;
    }
    delete this.availabilityData.tradeIn;
  };
}
